<script setup>
import { computed, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationCard from 'dashboard/components-next/Conversation/ConversationCard/ConversationCard.vue';

const { t } = useI18n();
const route = useRoute();

const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const contactsById = useMapGetter('contacts/getContactById');
const stateInbox = useMapGetter('inboxes/getInboxById');
const accountLabels = useMapGetter('labels/getLabels');

const accountLabelsValue = computed(() => accountLabels.value);

const uiFlags = useMapGetter('contactConversations/getUIFlags');
const isFetching = computed(() => uiFlags.value.isFetching);
const isFetchingProtocols = ref(false);
const attendanceProtocols = ref([]);

const contactConversations = computed(() =>
  conversations.value(route.params.contactId)
);

const fetchAttendanceProtocols = async contactId => {
  if (!contactId) return;

  isFetchingProtocols.value = true;
  try {
    const { data } = await ContactAPI.getAttendanceProtocols(contactId);
    attendanceProtocols.value = data.payload || [];
  } catch {
    attendanceProtocols.value = [];
  } finally {
    isFetchingProtocols.value = false;
  }
};

const generatedBy = item => {
  if (item.generated_by_name) return item.generated_by_name;
  if (item.source === 'resolution') {
    return t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.SOURCE_RESOLUTION');
  }
  return t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.SOURCE_SYSTEM');
};

const formattedDate = item => {
  if (!item.generated_at) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(item.generated_at));
};

const protocolDetails = item =>
  [
    formattedDate(item),
    generatedBy(item),
    item.conversation_display_id
      ? t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.CONVERSATION_LABEL', {
          id: item.conversation_display_id,
        })
      : '',
  ]
    .filter(Boolean)
    .join(' · ');

const copyProtocol = async protocol => {
  await copyTextToClipboard(protocol);
  useAlert(t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.COPIED'));
};

watch(
  () => route.params.contactId,
  contactId => fetchAttendanceProtocols(contactId),
  { immediate: true }
);
</script>

<template>
  <div
    v-if="isFetching"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else class="grid gap-6 py-5">
    <section class="grid gap-3 px-6">
      <div class="flex items-center justify-between gap-3">
        <h3 class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.HISTORY_TITLE') }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{ attendanceProtocols.length }}
        </span>
      </div>

      <div
        v-if="isFetchingProtocols"
        class="flex items-center justify-center py-6 text-n-slate-11"
      >
        <Spinner />
      </div>
      <div v-else-if="attendanceProtocols.length" class="grid gap-2">
        <article
          v-for="item in attendanceProtocols"
          :key="`${item.protocol}-${item.generated_at}`"
          class="rounded-xl border border-n-weak bg-n-alpha-1 px-4 py-3"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p
                class="mb-1 break-all font-mono text-sm font-semibold text-n-slate-12"
              >
                {{ item.protocol }}
              </p>
              <p class="mb-0 text-xs text-n-slate-10">
                {{ protocolDetails(item) }}
              </p>
            </div>
            <button
              type="button"
              class="shrink-0 text-n-slate-10 transition hover:text-n-brand"
              :aria-label="t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.COPY')"
              @click="copyProtocol(item.protocol)"
            >
              <span class="i-lucide-copy size-4" />
            </button>
          </div>
        </article>
      </div>
      <p v-else class="mb-0 py-4 text-sm text-n-slate-10">
        {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.HISTORY_EMPTY') }}
      </p>
    </section>

    <section>
      <div
        v-if="contactConversations.length > 0"
        class="px-6 divide-y divide-n-strong [&>*:hover]:!border-y-transparent [&>*:hover+*]:!border-t-transparent"
      >
        <ConversationCard
          v-for="conversation in contactConversations"
          :key="conversation.id"
          :conversation="conversation"
          :contact="contactsById(conversation.meta.sender.id)"
          :state-inbox="stateInbox(conversation.inboxId)"
          :account-labels="accountLabelsValue"
          class="rounded-none hover:rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3"
        />
      </div>
      <p
        v-else
        class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
      >
        {{ t('CONTACTS_LAYOUT.SIDEBAR.HISTORY.EMPTY_STATE') }}
      </p>
    </section>
  </div>
</template>
